'use client';
import {
  type FundingOperation,
  type FundingOperationDetail,
  type FundingQuotedAmounts,
  formatFiatUnits,
  fundingOperationAmounts,
  fundingOperationPresentation,
  fundingReceivedPresentation,
} from '@morfi/onramp-sdk';
import { useLocale, useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { AnimatedButton } from '@/components/ui/animated-button';
import { CircleIcon, Loading03Icon, Tick02Icon } from '@/components/ui/icon/glyphs';
import { Icon } from '@/components/ui/icon/index';

export function FundingOperationProgress({
  operation,
  quoted,
  onReceipt,
  onTryAnotherMethod,
}: {
  operation: FundingOperationDetail | FundingOperation;
  quoted?: FundingQuotedAmounts;
  onReceipt(): void;
  onTryAnotherMethod(): void;
}): ReactNode {
  const t = useTranslations('onramp');
  const locale = useLocale();
  const { phase, timeline, stages } = fundingOperationPresentation(operation);
  const initiating = ['created', 'awaiting_payment'].includes(operation.status);
  const complete = phase === 'complete';
  const amounts = fundingOperationAmounts(operation, quoted);
  const format = (units: string | null): string =>
    units === null
      ? t('amount_pending')
      : 'exchangeSource' in operation ||
          'cryptoDepositSource' in operation ||
          'connectedWalletSource' in operation
        ? t('exchange_fees_unknown')
        : formatFiatUnits(units, operation.fiatDecimals, operation.fiatCurrency, locale);
  const receiveValue = fundingReceivedPresentation(operation, locale, quoted);
  const received =
    phase === 'sandbox_complete'
      ? t('receipt_delivery_sandbox')
      : (receiveValue.value ?? t('amount_pending'));
  const titles = {
    processing: t(operation.purchase ? 'direct_purchase_pending' : 'progress_processing_title'),
    complete: operation.purchase ? t('direct_purchase_received') : t('progress_complete_title'),
    sandbox_complete: t('progress_sandbox_complete_title'),
    unknown: initiating ? t('progress_starting_title') : t('progress_processing_title'),
    failed: t('progress_failed_title'),
    cancelled: t('progress_cancelled_title'),
    refunded: t('progress_refunded_title'),
  };
  const descriptions = {
    processing:
      operation.status === 'delivered'
        ? operation.walletDelivery?.state === 'sandbox_confirmed'
          ? t('wallet_delivery_sandbox_confirmed')
          : t('wallet_delivery_unconfirmed')
        : operation.method === 'card'
          ? t('progress_card_processing_body')
          : t('progress_processing_body'),
    complete: t('progress_complete_body', { amount: received }),
    sandbox_complete: t('wallet_delivery_sandbox_confirmed'),
    unknown: initiating ? t('progress_starting_body') : t('progress_unknown_body'),
    failed: t('progress_failed_body'),
    cancelled: t('progress_cancelled_body'),
    refunded: t('progress_refunded_body'),
  };
  const art =
    complete || phase === 'sandbox_complete'
      ? 'success'
      : phase === 'failed' || phase === 'cancelled'
        ? 'declined'
        : phase === 'refunded'
          ? 'refund'
          : phase === 'unknown' && !initiating
            ? 'unknown'
            : 'processing';
  const steps = [
    [
      t('progress_started'),
      stages[0] !== 'complete'
        ? initiating
          ? t('progress_starting_body')
          : t('progress_unknown_body')
        : operation.method === 'card'
          ? t('progress_submitted')
          : 'exchangeSource' in operation ||
              'cryptoDepositSource' in operation ||
              'connectedWalletSource' in operation ||
              operation.chargedUnits === null
            ? t('progress_authorized')
            : `${format(amounts.chargedUnits)} · ${t('progress_authorized')}`,
    ],
    [
      t('progress_processing'),
      stages[1] === 'complete' ? t('progress_processed') : t('progress_waiting'),
    ],
    [
      t('progress_delivered'),
      stages[2] === 'complete'
        ? phase === 'sandbox_complete'
          ? t('receipt_delivery_sandbox')
          : t('progress_wallet_amount', { amount: received })
        : t('progress_awaiting_delivery'),
    ],
  ];
  return (
    <div className="flex flex-col gap-4.5 pt-2" aria-live="polite">
      <div className="flex items-center gap-4">
        <OptimizedImage
          // ALLOW-NSFW-FALSE: bundled approved payment status artwork.
          nsfw={operation.purchase?.imageNsfw ?? false}
          variant="thumbnail"
          // ALLOW-NO-PREVIEW: status artwork is decorative, not expandable media.
          preview={false}
          loadReveal="none"
          rounded=""
          imageClassName="object-contain"
          src={operation.purchase?.imageUrl || `/assets/onramp/status-${art}.png`}
          alt=""
          size="size-18"
          className="shrink-0 bg-transparent"
        />
        <div>
          <h3 className="text-lg font-medium leading-6">{titles[phase]}</h3>
          {operation.purchase && <p className="text-body">{operation.purchase.title}</p>}
          <p className="mt-1.5 text-caption leading-4.5 text-foreground-primary">
            {descriptions[phase]}
          </p>
        </div>
      </div>
      {timeline ? (
        <>
          <ol>
            {steps.map(([title, description], index) => (
              <li
                key={title}
                className="flex items-start gap-3 border-b border-border-primary py-4 text-body last:border-0"
              >
                <span
                  aria-hidden="true"
                  className={`grid size-7 shrink-0 place-items-center rounded-full ${stages[index] === 'complete' ? 'bg-transparent text-feedback-success-content' : stages[index] === 'loading' ? 'bg-background-brand text-foreground-brand' : 'bg-background-tertiary text-foreground-primary'}`}
                >
                  <Icon
                    icon={
                      stages[index] === 'complete'
                        ? Tick02Icon
                        : stages[index] === 'loading'
                          ? Loading03Icon
                          : CircleIcon
                    }
                    className={
                      stages[index] === 'loading'
                        ? 'size-5 animate-spin motion-reduce:animate-none'
                        : 'size-5'
                    }
                  />
                </span>
                <div>
                  <strong className="text-body font-medium">{title}</strong>
                  <p className="text-caption leading-4.5 text-foreground-primary">{description}</p>
                </div>
                <small className="ml-auto whitespace-nowrap text-micro leading-5 text-foreground-primary">
                  {stages[index] === 'complete'
                    ? t('progress_confirmed')
                    : stages[index] === 'loading'
                      ? t('progress_in_progress')
                      : t('progress_pending')}
                </small>
              </li>
            ))}
          </ol>
          <dl className="border-t border-border-primary">
            {[
              [t('progress_you_pay'), format(amounts.chargedUnits)],
              [t('fees'), format(amounts.feeUnits)],
              [
                receiveValue.estimated ? t('estimated_receive') : t('progress_you_receive'),
                received,
              ],
            ].map(([label, value]) => (
              <div
                key={label}
                className="flex justify-between gap-4 border-b border-border-primary py-3 text-body"
              >
                <dt>{label}</dt>
                <dd className="font-medium">{value}</dd>
              </div>
            ))}
          </dl>
        </>
      ) : null}
      {phase !== 'unknown' && (phase !== 'processing' || operation.status === 'delivered') ? (
        <AnimatedButton
          className="h-11 rounded-control"
          onClick={phase === 'failed' ? onTryAnotherMethod : onReceipt}
        >
          {phase === 'failed' ? t('progress_try_another_method') : t('progress_view_receipt')}
        </AnimatedButton>
      ) : null}
    </div>
  );
}
